<template>
  <ConfigurationHeader
    :canCreate="true"
    :endPointData="endPointData"
    loadScreen="equipmentProcessConfiguration"
    :textData="textData"
    @search="searchWord"
    @show-search="toggleSearch"
    @sort="sortType"
  />

  <HeaderBar
    v-if="searchResults"
    :modifiers="['narrow', isDesktop() && 'no-padding']"
  >
    <p class="configuration-info typo-ignore typo-small-title">
      <strong>{{ $t(endPointData.nav) }}</strong>
      <span class="typo-tiny">{{ $t("shafts.configured", { total: searchResults.length }) }}</span>
    </p>
  </HeaderBar>

  <div
    :class="{
      'configuration-group': true,
      'configuration-group--padding': !isDesktop(),
      'configuration-group--default-normal': !showSearch,
      'configuration-group--default-small': showSearch,
    }"
  >
    <template
      v-for="station in searchResults"
      :key="station.id"
    >
      <ConfigurationItem
        :endpointIdentifier="endPointData.endpointIdentifier"
        :listItem="station"
        :operationType="endPointData.operationType"
        :organisationId="$route.params.organisationId"
        @delete-item="deleteItem"
      />
    </template>
  </div>
  <div class="configuration-new hide-for-medium">
    <router-link
      class="button"
      :to="{ name: endPointData.newFormRoute }"
    >
      <div class="button__icon button--margin-right">
        <PlusIcon />
      </div>
      {{ textData.new }}
    </router-link>
  </div>
</template>
<script setup>
import { ref, onMounted, watch } from 'vue';

// Utils
import { useRoute } from 'vue-router';
import orderBy from '@/functions/order_by';
import useAjax from '@/functions/use_ajax';
import isDesktop from '@/functions/responsive';

// Components
import Searcher from '@/functions/searcher';
import HeaderBar from '@/components/generic/header_bar.vue';
import ConfigurationItem from '@/components/generic/configuration_item.vue';
import ConfigurationHeader from '@/components/generic/configuration_header_template.vue';

// Svg
import PlusIcon from '@/images/plus-16.svg';

const props = defineProps({
  /** Hash containing endpoints */
  endPointData: { type: Object, required: true, default: null },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
});

const route = useRoute();
const ajax = useAjax();
const search = ref(null);
const searchResults = ref(null);
const loading = ref(null);
const sortBy = ref(null);
const showSearch = ref(false);
const stationList = ref(null);
let searcher;
let searchKeys = ['name'];
const byName = orderBy('name', x => x.toLowerCase());

onMounted(() => {
  loadStation();
});

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => sortResults());

watch(() => stationList, buildSearcher);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher?.search(search.value);
  } else {
    searchResults.value = stationList.value;
  }

  sortResults();
});

function sortResults() {
  let sortFn;
  switch (sortBy.value) {
    case 'alphabetical': sortFn = byName.asc; break;
    case 'alphabetical_reverse': sortFn = byName.desc; break;
  }
  searchResults.value.sort(sortFn);
}

function buildSearcher(items = stationList.value) {
  searcher = new Searcher(items, { keys: searchKeys });
}

async function loadStation() {
  if (route.params.shaftId) {
    loading.value = true;
    let { ok, data } = await ajax(props.endPointData.stationGroupsEndpoint);

    if (ok) {
      stationList.value = data;
      searchResults.value = data;
      loading.value = false;
      buildSearcher();
    }
  }
}

function deleteItem(event) {
  stationList.value = stationList.value.filter(item => item.id !== event);
  searchResults.value = stationList.value;
}

function sortType(data) {
  sortBy.value = data;
}

function searchWord(data) {
  search.value = data;
}

function toggleSearch(data) {
  showSearch.value = data;
}
</script>
